/**
 * `src` 를 비동기 결과로 변환하는 작업을 캐시·동시성 제한·refCount 와 함께 다루는 범용 큐.
 *
 * 책임
 * - **결과 캐시 (LRU)**: 같은 src 의 결과 (TResult) 를 한 번만 만든다.
 * - **In-flight dedup**: 같은 src 의 동시 acquire 는 하나의 작업으로 합쳐진다.
 * - **참조 카운팅 (soft LRU)**: 사용 중인 (refCount > 0) entry 는 evict 대상에서 제외하여,
 *   화면에 깨진 결과가 남는 사고를 차단한다. 일시적으로 maxCacheSize 를 초과할 수 있다.
 * - **동시성 제한**: 동시에 진행할 수 있는 run() 작업 개수를 제한한다.
 * - **백그라운드 일시정지**: `document.hidden` 일 때 신규 작업을 멈춰 OS throttle 을 피한다.
 * - **에러 자동 재시도**: 재 acquire 시 status="error" 면 idle 로 되돌리고 다시 시도하되,
 *   `maxAttempts` 캡으로 무한 재시도를 막는다.
 *
 * 비책임
 * - 결과의 의미 (Object URL, 디코드된 이미지, 등) 와 그 정리 방법은 알지 못한다.
 *   외부 자원(blob URL 등) 을 가진 결과는 `onCleanup` 에서 정리해야 한다.
 *
 * 사용처
 * - `HeicConversionQueue` (TResult = jpeg blob URL)
 * - `ImageCache` (TResult = { url, warmImage })
 */
export type ResourceStatus = "idle" | "running" | "success" | "error";

export interface ResourceSnapshot<TResult> {
  readonly src: string;
  readonly status: ResourceStatus;
  /** 작업 성공 시의 결과. 그 외 상태에선 undefined. */
  readonly result?: TResult;
  /** 작업 실패 시의 에러. */
  readonly error?: Error;
}

export type ResourceSubscriber<TResult> = (snapshot: ResourceSnapshot<TResult>) => void;

export interface RefCountedAsyncResourceQueueOptions<TResult> {
  /**
   * src 를 비동기 결과로 변환한다.
   *
   * 한 attempt 당 한 번씩 호출된다 (재 acquire 로 인한 retry 도 새 attempt).
   * 호출 도중 entry 가 evict / destroy 되면 (cache.get(key) !== entry) 결과는 즉시 폐기된다.
   */
  run: (key: string) => Promise<TResult>;
  /**
   * 결과(또는 entry 자체) 가 캐시에서 사라질 때 호출된다. 외부 자원 정리 용도.
   *
   * 호출 시점:
   * - LRU evict 후 (status === "success" 였다면 result 가 함께 전달)
   * - destroy() 호출 시 (남은 entry 들에 대해)
   * - run() 이 도중 evict 되어 결과가 도착했을 때 (해당 결과 한 건만)
   * - error retry 진입 시 entry.result 가 남아 있다면 (방어적)
   *
   * status 가 success 가 아니었던 entry 의 정리에서는 result 가 undefined 로 들어온다.
   * 외부 자원 (Object URL 등) 정리는 result 가 truthy 일 때만 의미 있는 것이 보통.
   */
  onCleanup?: (key: string, result: TResult | undefined) => void;
  concurrency?: number;
  maxCacheSize?: number;
  /**
   * 한 entry 의 생애 동안 허용할 최대 시도 횟수.
   *
   * 재 acquire 마다 자동 재시도하지만, 영구적인 실패 (깨진 파일 등) 에 대해 끝없이
   * 재시도하는 것을 막는다. cap 도달 시 entry 의 status="error" 가 잠긴다.
   * LRU evict 후 다음 acquire 는 fresh entry 라 카운터가 0 부터 다시 시작.
   *
   * 기본값 `Infinity` (캡 없음).
   */
  maxAttempts?: number;
  /** src → 캐시 키 매핑. 기본값 항등함수. */
  getCacheKey?: (src: string) => string;
  /**
   * 백그라운드 탭(`document.hidden === true`) 에서 신규 작업을 일시 중지한다.
   *
   * 진행 중인 작업은 abort 되지 않고 그대로 완료된다. 대기 중인 작업은
   * foreground 복귀까지 보류된다. 기본값 true. document 가 없으면 자동 비활성.
   */
  pauseOnHidden?: boolean;
}

interface CacheEntry<TResult> {
  status: ResourceStatus;
  result?: TResult;
  error?: Error;
  /** 현재 이 entry 의 결과를 사용하는 구독자 수. 0 이 되어야 LRU evict 후보. */
  refCount: number;
  subscribers: Set<ResourceSubscriber<TResult>>;
  /** 이 entry 가 살아있는 동안 시도된 run() 횟수. */
  attemptCount: number;
}

interface ResolvedOptions<TResult> {
  run: (key: string) => Promise<TResult>;
  onCleanup: (key: string, result: TResult | undefined) => void;
  maxCacheSize: number;
  maxAttempts: number;
  getCacheKey: (src: string) => string;
}

const DEFAULT_CONCURRENCY = 1;
const DEFAULT_MAX_CACHE_SIZE = 50;
const DEFAULT_MAX_ATTEMPTS = Number.POSITIVE_INFINITY;

export class RefCountedAsyncResourceQueue<TResult> {
  /** 삽입 순서를 LRU 순서로 활용 (가장 앞이 가장 오래됨) */
  private _cache = new Map<string, CacheEntry<TResult>>();
  private _options: ResolvedOptions<TResult>;
  /** 사용자가 의도한 최대 동시성 (백그라운드 일시정지 중에도 보존) */
  private _concurrency: number;
  private _isHidden = false;
  private _detachVisibility: (() => void) | null = null;

  private _activeCount = 0;
  /** 동시성 제한으로 대기 중인 캐시 키 */
  private _pendingQueue: string[] = [];

  constructor(options: RefCountedAsyncResourceQueueOptions<TResult>) {
    this._options = {
      run: options.run,
      onCleanup: options.onCleanup ?? (() => {}),
      maxCacheSize: options.maxCacheSize ?? DEFAULT_MAX_CACHE_SIZE,
      maxAttempts: options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
      getCacheKey: options.getCacheKey ?? ((src) => src),
    };
    this._concurrency = options.concurrency ?? DEFAULT_CONCURRENCY;

    if ((options.pauseOnHidden ?? true) && typeof document !== "undefined") {
      this._attachVisibilityListener();
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * 캐시에 저장된 결과를 부수효과 없이 조회한다.
   *
   * - LRU 순서를 갱신하지 않고, refCount 도 변경하지 않는다.
   * - React 컴포넌트의 `useState` 초기값에서 캐시 hit 여부를 체크할 때 사용.
   */
  peek(src: string): ResourceSnapshot<TResult> | null {
    const key = this._options.getCacheKey(src);
    const entry = this._cache.get(key);
    if (!entry) return null;
    return this._snapshot(key, entry);
  }

  /**
   * 결과를 구독한다.
   *
   * - 캐시에 결과가 있으면 즉시 현재 스냅샷으로 콜백.
   * - 없으면 entry 를 만들어 큐에 등록하고 status 변화마다 콜백.
   * - 호출 즉시 refCount 가 1 증가하므로, 반환된 release 함수를 반드시 호출해야 한다.
   *
   * @returns 구독 해제 함수. 호출 시 refCount 1 감소 + 필요 시 evict 시도. 멱등.
   */
  acquire(src: string, subscriber: ResourceSubscriber<TResult>): () => void {
    const key = this._options.getCacheKey(src);
    let entry = this._cache.get(key);

    if (entry) {
      // LRU touch
      this._cache.delete(key);
      this._cache.set(key, entry);

      // 캐시된 error 는 stale 신호로 본다. 재 acquire 자체가 retry 시그널이므로
      // 같은 entry 를 그대로 재활용해 idle 로 되돌리고 큐에 다시 넣는다.
      // 기존 subscribers 는 그대로 유지되어 새 결과 (success/error) 를 다시 통보받는다.
      // 단 maxAttempts 캡 도달 시엔 error 를 그대로 유지해 추가 작업을 발생시키지 않는다.
      if (entry.status === "error" && entry.attemptCount < this._options.maxAttempts) {
        // 방어적: 이론상 error 상태에서 result 는 없지만 안전하게 정리.
        if (entry.result !== undefined) {
          this._options.onCleanup(key, entry.result);
          entry.result = undefined;
        }
        entry.error = undefined;
        entry.status = "idle";
        this._pendingQueue.push(key);
      }
    } else {
      entry = {
        status: "idle",
        refCount: 0,
        subscribers: new Set(),
        attemptCount: 0,
      };
      this._cache.set(key, entry);
      this._pendingQueue.push(key);
    }

    entry.refCount += 1;
    entry.subscribers.add(subscriber);
    subscriber(this._snapshot(key, entry));

    this._evictIfNeeded();
    this._drain();

    let released = false;
    return () => {
      if (released) return;
      released = true;
      this._release(key, subscriber);
    };
  }

  updateOptions(options: Partial<RefCountedAsyncResourceQueueOptions<TResult>>): void {
    if (options.concurrency !== undefined) this._concurrency = options.concurrency;
    if (options.run !== undefined) this._options.run = options.run;
    if (options.onCleanup !== undefined) this._options.onCleanup = options.onCleanup;
    if (options.maxCacheSize !== undefined) this._options.maxCacheSize = options.maxCacheSize;
    if (options.maxAttempts !== undefined) this._options.maxAttempts = options.maxAttempts;
    if (options.getCacheKey !== undefined) this._options.getCacheKey = options.getCacheKey;
    this._drain();
    this._evictIfNeeded();
  }

  /**
   * 큐 전체를 즉시 정리한다.
   *
   * 진행 중인 작업은 abort 할 수 없으므로 결과는 도착 즉시 폐기된다 (cleanup 호출).
   * 모든 entry 의 result 는 onCleanup 으로 전달되어 외부 자원이 회수된다.
   */
  destroy(): void {
    this._detachVisibility?.();
    this._detachVisibility = null;

    for (const [key, entry] of this._cache) {
      this._options.onCleanup(key, entry.status === "success" ? entry.result : undefined);
      entry.subscribers.clear();
    }
    this._cache.clear();
    this._pendingQueue = [];
    this._activeCount = 0;
  }

  /** 테스트/디버깅용 내부 상태 스냅샷. */
  getStats(): {
    cacheSize: number;
    activeCount: number;
    pendingCount: number;
    concurrency: number;
    isHidden: boolean;
  } {
    return {
      cacheSize: this._cache.size,
      activeCount: this._activeCount,
      pendingCount: this._pendingQueue.length,
      concurrency: this._concurrency,
      isHidden: this._isHidden,
    };
  }

  /* ------------------------------------------------------------------ */
  /*  Private                                                            */
  /* ------------------------------------------------------------------ */

  private _release(key: string, subscriber: ResourceSubscriber<TResult>): void {
    const entry = this._cache.get(key);
    if (!entry) return;

    entry.subscribers.delete(subscriber);
    entry.refCount = Math.max(0, entry.refCount - 1);

    if (entry.refCount === 0) this._evictIfNeeded();
  }

  /**
   * 캐시 크기가 maxCacheSize 를 초과하면 LRU 순서로 evict.
   *
   * 다음 항목은 evict 대상에서 제외된다:
   * - refCount > 0 (현재 사용 중인 컴포넌트가 있음)
   * - status === "running" (작업 진행 중; 완료 직후 폐기되는 사고 회피)
   *
   * 이 정책 때문에 일시적으로 maxCacheSize 를 초과할 수 있으며, 의도된 동작이다.
   */
  private _evictIfNeeded(): void {
    if (this._cache.size <= this._options.maxCacheSize) return;

    for (const [key, entry] of this._cache) {
      if (this._cache.size <= this._options.maxCacheSize) return;

      const isEvictable = entry.refCount === 0 && entry.status !== "running";
      if (!isEvictable) continue;

      this._evictEntry(key, entry);
    }
  }

  private _evictEntry(key: string, entry: CacheEntry<TResult>): void {
    this._options.onCleanup(key, entry.status === "success" ? entry.result : undefined);
    this._cache.delete(key);

    const pendingIndex = this._pendingQueue.indexOf(key);
    if (pendingIndex !== -1) this._pendingQueue.splice(pendingIndex, 1);
  }

  /** visibility 일시정지 중에는 0 으로 줄어든다. */
  private get _effectiveConcurrency(): number {
    return this._isHidden ? 0 : this._concurrency;
  }

  private _drain(): void {
    while (this._activeCount < this._effectiveConcurrency && this._pendingQueue.length > 0) {
      const key = this._pendingQueue.shift();
      if (key === undefined) return;

      const entry = this._cache.get(key);
      if (!entry || entry.status !== "idle") continue;

      this._activeCount += 1;
      void this._runEntry(key, entry);
    }
  }

  private async _runEntry(key: string, entry: CacheEntry<TResult>): Promise<void> {
    entry.attemptCount += 1;
    entry.status = "running";
    this._notify(key, entry);

    try {
      const result = await this._options.run(key);

      // run 도중 evict / destroy 됐다면 결과를 즉시 폐기
      if (this._cache.get(key) !== entry) {
        this._options.onCleanup(key, result);
        return;
      }

      entry.status = "success";
      entry.result = result;
      this._notify(key, entry);
    } catch (rawError) {
      if (this._cache.get(key) !== entry) return;

      entry.status = "error";
      entry.error = rawError instanceof Error ? rawError : new Error(String(rawError));
      this._notify(key, entry);
    } finally {
      this._activeCount = Math.max(0, this._activeCount - 1);
      this._drain();
    }
  }

  private _attachVisibilityListener(): void {
    const handler = () => {
      const wasHidden = this._isHidden;
      this._isHidden = document.hidden;
      // foreground 복귀 시 보류된 작업을 즉시 진행
      if (wasHidden && !this._isHidden) this._drain();
    };
    this._isHidden = document.hidden;
    document.addEventListener("visibilitychange", handler);
    this._detachVisibility = () => document.removeEventListener("visibilitychange", handler);
  }

  private _notify(key: string, entry: CacheEntry<TResult>): void {
    const snapshot = this._snapshot(key, entry);
    for (const subscriber of entry.subscribers) subscriber(snapshot);
  }

  private _snapshot(key: string, entry: CacheEntry<TResult>): ResourceSnapshot<TResult> {
    return {
      src: key,
      status: entry.status,
      result: entry.result,
      error: entry.error,
    };
  }
}
